import PropTypes from 'prop-types';
import {Fragment} from 'react';

import {Box, Container as BaseContainer, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component as ContentfulComponent} from '@amblique/king-living/app/components/contentful/component';
import ProductBazaarvoiceFallback from '@amblique/king-living/app/components/product-bazaarvoice/fallback';
import PdpPageSkeleton from '@amblique/king-living/app/components/pdp-page-skeleton';
import {usePageContext} from '@amblique/king-living/app/hooks/contentful';

import {getContainerFullWidthChakraProps, getContainerFullWidthClassName} from '@amblique/king-living/app/contentful/utils/container-full-width-chakra-props';
import {normalizeContainerFullWidth} from '@amblique/king-living/app/contentful/utils/normalize-container-full-width';

/**
 * Optional full-width wrapper when `backgroundColour` is set. That outer band uses
 * `className="component-container__background"` (Chakra still emits a hash class for Emotion styles).
 *
 * @param {Object} props
 * @param {Contentful.Container} props.component
 * @param {boolean} [props.isComplete]
 * @param {boolean} [props.isFetching]
 */
export const Container = ({component, isComplete = true, isFetching = false, className, ...props}) => {
  const {isProductLoading, isProductPage} = usePageContext() || {};
  const styles = useMultiStyleConfig('contentful/container', {component, isComplete, isFetching});
  const fullWidthMode = normalizeContainerFullWidth(component?.fullWidth);
  const fullWidthChakraProps = getContainerFullWidthChakraProps(fullWidthMode);
  const fullWidthClassName = getContainerFullWidthClassName(fullWidthMode);

  // Keep jump-link target attrs on the outermost node so sticky-header offsets apply correctly.
  const {id: jumpAnchorId, style: jumpAnchorStyle, ...layoutRestProps} = props;
  const hasBackgroundWrapper = Boolean(styles.wrapper?.backgroundColor);

  // `fullWidth: Yes` — render `Box` instead of Chakra `<Container>`. The shared `Container`
  // theme applies responsive `paddingLeft` / `paddingRight`; `px={0}` does not always win the
  // style merge, so `chakra-container` still showed horizontal padding in DevTools.
  const layoutProps = {
    ...styles.root,
    ...layoutRestProps,
    ...fullWidthChakraProps,
    className: [className, fullWidthClassName].filter(Boolean).join(' '),
    ...(!hasBackgroundWrapper ? {id: jumpAnchorId, style: jumpAnchorStyle} : {}),
  };

  // Contentful's GraphQL Delivery API returns `null` placeholders inside
  // collection arrays when a referenced entry is unpublished, archived, or
  // otherwise unavailable. Filter them out before dereferencing `child.sys.id`
  // (otherwise the whole page crashes with `Cannot read properties of null
  // (reading 'sys')`).
  const regionItems = (component.region1Collection?.items ?? []).filter(Boolean);

  // PDP skeleton: (1) Container CF fetch — slim stub, region1 empty; (2) gallery product — PageContext.isProductLoading from BasePage (allImages:true), not DynamicPage SEO slice.
  // No extra context/aggregate hook — ProductDetail below handles its own isFetching once region1 is populated.
  if (isProductPage && (isProductLoading || (isFetching && regionItems.length === 0))) {
    return <PdpPageSkeleton />;
  }

  const hasCmsBazaarvoiceReviews = regionItems.some(
    (child) => child.__typename === 'BazaarvoiceReviews'
  );

  const region = regionItems.map((child) => {
    if (
      child.__typename === 'ProductDetail' &&
      isProductPage &&
      !hasCmsBazaarvoiceReviews
    ) {
      return (
        <Fragment key={`component[${child.sys.id}]`}>
          <ContentfulComponent component={child} />
          <ProductBazaarvoiceFallback />
        </Fragment>
      );
    }

    return (
      <ContentfulComponent key={`component[${child.sys.id}]`} component={child} />
    );
  });

  const content =
    fullWidthMode === 'Yes' ?
      <Box {...layoutProps}>{region}</Box> :
      <BaseContainer {...layoutProps}>{region}</BaseContainer>;

  if (hasBackgroundWrapper) {
    return (
      <Box
        {...styles.wrapper}
        className="component-container__background"
        id={jumpAnchorId}
        style={jumpAnchorStyle}
      >
        {content}
      </Box>
    );
  }

  return content;
};

Container.displayName = 'Container';
Container.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
